import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserCheck } from "lucide-react";

/**
 * Renders when the accountant's session is actually a super-admin or accountant
 * impersonation. Clicking Exit swaps back to the original session.
 *
 * Detection: `/clients/me` returns `impersonating: true` when the
 * `return_access_token` cookie is present.
 */
export default function ImpersonationBanner() {
  const { user } = useAuth();
  const [impersonating, setImpersonating] = useState(false);
  const [label, setLabel] = useState("");
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/clients/me");
        if (!alive) return;
        setImpersonating(!!data.impersonating);
        setLabel(user?.business_name || user?.name || user?.email || "user");
      } catch {
        setImpersonating(false);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  if (!impersonating) return null;

  const exit = async () => {
    setExiting(true);
    try {
      await api.post("/clients/end-impersonation");
      // Hard reload so the AuthContext picks up the restored (admin/accountant) token
      window.location.href = "/";
    } catch {
      setExiting(false);
    }
  };

  return (
    <div
      className="bg-ochre text-white px-4 py-2 flex items-center justify-between gap-3 text-sm shrink-0"
      data-testid="impersonation-banner-app"
    >
      <div className="flex items-center gap-2 min-w-0">
        <UserCheck className="w-4 h-4 shrink-0" strokeWidth={2} />
        <span className="truncate">
          Viewing as <b>{label}</b>. Any actions you take are recorded to their account.
        </span>
      </div>
      <button
        onClick={exit}
        disabled={exiting}
        className="bg-white text-ochre font-medium px-3 py-1 rounded-md text-xs hover:bg-white/90 disabled:opacity-70 shrink-0"
        data-testid="exit-impersonation-app-btn"
      >
        {exiting ? "Returning…" : "Exit impersonation"}
      </button>
    </div>
  );
}
